import type { SupabaseClient } from '../../lib/supabase';

/**
 * The Alex Huddle chat in Messages (KYU_DECISION 25 Sept 20:10; plan-drop3b.md
 * 3b-2). Nothing is copied: my_huddle_chat (20270181000100) reads the comments
 * Alex's huddle profile left on the tasks of the caller's live huddle-link
 * section, and only the link's owner gets rows. The page groups them per task
 * (one thread per task, linking to the task). A reply is an ordinary task
 * comment on that task, through the same path every task thread uses, so
 * Alex sees it where he already does.
 */

export interface HuddleChatMessage {
  commentId: string;
  taskId: string;
  taskTitle: string;
  projectId: string;
  authorId: string;
  authorName: string;
  authorAvatarUrl: string | null;
  body: string;
  createdAt: string;
  editedAt: string | null;
}

export interface HuddleThread {
  taskId: string;
  taskTitle: string;
  projectId: string;
  /** Oldest first, the way the RPC returns them. */
  messages: HuddleChatMessage[];
  /** created_at of the newest message, for ordering threads. */
  latestAt: string;
}

/** The Messages sidebar entry id: never a project id, so it cannot clash. */
export const HUDDLE_CHAT_ID = 'alex-huddle';

export async function fetchHuddleChat(client: SupabaseClient): Promise<HuddleChatMessage[]> {
  const { data, error } = await client.rpc('my_huddle_chat', {});
  if (error) {
    throw new Error(error.message);
  }
  return (Array.isArray(data) ? data : []).map((row) => ({
    commentId: row.comment_id,
    taskId: row.task_id,
    taskTitle: row.task_title,
    projectId: row.project_id,
    authorId: row.author_id,
    authorName: row.author_name ?? '',
    authorAvatarUrl: row.author_avatar_url ?? null,
    body: row.body,
    createdAt: row.created_at,
    editedAt: row.edited_at ?? null,
  }));
}

/** One thread per task, the most recently active thread first. */
export function groupHuddleThreads(messages: HuddleChatMessage[]): HuddleThread[] {
  const byTask = new Map<string, HuddleThread>();
  for (const message of messages) {
    const thread = byTask.get(message.taskId) ?? {
      taskId: message.taskId,
      taskTitle: message.taskTitle,
      projectId: message.projectId,
      messages: [],
      latestAt: '',
    };
    thread.messages.push(message);
    if (message.createdAt > thread.latestAt) {
      thread.latestAt = message.createdAt;
    }
    byTask.set(message.taskId, thread);
  }
  for (const thread of byTask.values()) {
    thread.messages.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }
  return [...byTask.values()].sort((a, b) => b.latestAt.localeCompare(a.latestAt));
}

/** The task a thread links to: the ordinary task deep link (ProjectPage ?task=). */
export function huddleTaskHref(thread: { projectId: string; taskId: string }): string {
  return `/projects/${encodeURIComponent(thread.projectId)}?task=${encodeURIComponent(thread.taskId)}`;
}

export interface HuddleChatUnread {
  /** Show the Alex Huddle entry at all (the caller owns a live huddle link). */
  hasChat: boolean;
  /** Capped at 99 by the RPC. */
  unread: number;
}

export async function fetchHuddleChatUnread(client: SupabaseClient): Promise<HuddleChatUnread> {
  const { data, error } = await client.rpc('my_huddle_chat_unread');
  if (error) {
    throw new Error(error.message);
  }
  const row = Array.isArray(data) ? data[0] : undefined;
  if (row === undefined || row === null || row.has_chat !== true) {
    return { hasChat: false, unread: 0 };
  }
  const unread = typeof row.unread === 'number' && row.unread > 0 ? row.unread : 0;
  return { hasChat: true, unread };
}

export async function markHuddleChatRead(client: SupabaseClient): Promise<void> {
  const { error } = await client.rpc('mark_huddle_chat_read');
  if (error) {
    throw new Error(error.message);
  }
}

/**
 * The room counts plus the Alex Huddle chat's, for the Messages total in the
 * app sidebar (plan-drop3b.md:66, "3b-2 adds the Alex Huddle chat to that
 * sum"). Only counted when the entry exists (has_chat).
 */
export function withHuddleUnread(
  roomCounts: Map<string, number> | undefined,
  huddle: HuddleChatUnread | undefined,
): Map<string, number> | undefined {
  if (huddle === undefined || !huddle.hasChat) {
    return roomCounts;
  }
  const out = new Map(roomCounts ?? []);
  out.set(HUDDLE_CHAT_ID, huddle.unread);
  return out;
}
